import { useState, useEffect, useCallback, useRef } from 'react';
import { salesAPI } from '../../../services';
import { handleValidationErrors } from '../../../utils/errorHandlers';
import { toast } from 'react-toastify';

export const useViewSalesData = (saleId) => {
  const [sale, setSale] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const isLoadingRef = useRef(false);
  const prevSaleIdRef = useRef(null);

  // Load sale data from API
  const loadSaleData = useCallback(async (forceRefresh = false) => {
    if (!saleId) return;

    // Prevent concurrent calls
    if (isLoadingRef.current) {
      return;
    }

    // Check if saleId actually changed (unless forced refresh)
    if (!forceRefresh && prevSaleIdRef.current === saleId && prevSaleIdRef.current !== null) {
      // SaleId hasn't changed, skip API call
      return;
    }
    
    try {
      isLoadingRef.current = true;
      prevSaleIdRef.current = saleId;
      setLoading(true);
      setError(null);
      
      const response = await salesAPI.getSaleById(saleId);
      
      // Extract data from the wrapped response
      const saleData = response.data || response;
      
      // Transform API data to match component expectations
      const transformedSale = {
        id: saleData.id,
        invoiceId: saleData.invoice_id,
        customerName: saleData.customer_name,
        creator_name: saleData.creator_name,
        amount: parseFloat(saleData.total_amount) || 0,
        status: saleData.status?.toLowerCase() || 'draft',
        statusDisplay: saleData.status_display || saleData.status,
        previousBalance: parseFloat(saleData.previous_balance) || 0,
        paidAmount: parseFloat(saleData.paid_amount) || 0,
        totalQty: saleData.total_qty || 0,
        shippingAmount: parseFloat(saleData.shipping_amount) || 0,
        flatDiscount: parseFloat(saleData.flat_discount) || 0,
        netProfit: parseFloat(saleData.net_profit) || 0,
        subTotal: parseFloat(saleData.sub_total) || 0,
        resultingBalance: parseFloat(saleData.resulting_balance) || 0,
        sendByPost: saleData.send_by_post || false,
        customer: saleData.customer,
        createdAt: saleData.created_at,
        updatedAt: saleData.updated_at
      };
      
      // Transform cart items to products - reverse order to show newest first
      const transformedProducts = saleData.cart_items?.slice().reverse().map(item => ({
        id: item.id,
        productId: item.product,
        name: item.product_detail?.name || 'Unknown Product',
        brand: item.product_detail?.brand_name || '',
        model: item.product_detail?.model_name || '',
        category: item.product_detail?.category_name || 'Mobile',
        quantity: item.qty || 0,
        sellPrice: parseFloat(item.price) || 0,
        discounted_sub_total: parseFloat(item.discounted_sub_total) || 0,
        buyPrice: parseFloat(item.buying_price) || 0,
        flatDiscount: parseFloat(item.flat_discount) || 0,
        effectivePrice: parseFloat(item.effective_price) || 0,
        subTotal: parseFloat(item.sub_total) || 0,
        image: item.product_detail?.image || null,
        unique_id: item.unique_id || item.product_detail?.unique_id || null, // Include unique_id if available
        in_stock: item.product_detail?.in_stock || 0, // Add in_stock for stock validation
        product_detail: item.product_detail, // Include full product_detail for display_name
        // Add product details for ProductTable format
        ram: item.product_detail?.ram || '',
        storage: item.product_detail?.storage || '',
        variants: item.product_detail?.variants || [],
        suffix: item.product_detail?.suffix || '',
        used_mobile: item.product_detail?.used_mobile
      })) || [];
      
      setSale(transformedSale);
      setProducts(transformedProducts);
      
    } catch (err) {
      console.error('Error loading sale data:', err);
      setError(err.message || 'Failed to load sale data');
      handleValidationErrors(err, 'Failed to load sale data');
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  }, [saleId]);

  // Publish sale
  const handlePublishSale = useCallback(async () => {
    if (!saleId) return;
    
    try {
      setActionLoading(true);
      const response = await salesAPI.publishSale(saleId);
      
      // Update local state
      setSale(prev => prev ? {
        ...prev,
        status: 'published',
        statusDisplay: 'Published'
      } : null);
      
      toast.success(response?.message || 'Sale published successfully');
      
      // Reload data to get updated information (force refresh)
      await loadSaleData(true);
      
    } catch (err) {
      console.error('Error publishing sale:', err);
      handleValidationErrors(err, 'Failed to publish sale');
    } finally {
      setActionLoading(false);
    }
  }, [saleId, loadSaleData]);

  // Cancel sale
  const handleCancelSale = useCallback(async () => {
    if (!saleId) return;
    
    try {
      setActionLoading(true);
      const response = await salesAPI.cancelSale(saleId);
      
      // Update local state
      setSale(prev => prev ? {
        ...prev,
        status: 'cancelled',
        statusDisplay: 'Cancelled'
      } : null);
      
      toast.success(response?.message || 'Sale cancelled successfully');
      
      // Reload data to get updated information (force refresh)
      await loadSaleData(true);
      
    } catch (err) {
      console.error('Error cancelling sale:', err);
      handleValidationErrors(err, 'Failed to cancel sale');
    } finally {
      setActionLoading(false);
    }
  }, [saleId, loadSaleData]);

  // Update sale status
  const handleUpdateStatus = useCallback(async (newStatus) => {
    if (!saleId) return;
    
    try {
      setActionLoading(true);
      const response = await salesAPI.updateSaleStatus(saleId, newStatus);
      
      // Update local state
      setSale(prev => prev ? {
        ...prev,
        status: newStatus.toLowerCase(),
        statusDisplay: newStatus
      } : null);
      
      toast.success(response?.message || 'Sale status updated successfully');
      
      // Reload data to get updated information (force refresh)
      await loadSaleData(true);
      
    } catch (err) {
      console.error('Error updating sale status:', err);
      handleValidationErrors(err, 'Failed to update sale status');
    } finally {
      setActionLoading(false);
    }
  }, [saleId, loadSaleData]);

  // Update product quantity locally
  const updateProductQuantity = useCallback((productId, newQuantity) => {
    if (newQuantity < 1) return;
    
    setProducts(prev => prev.map(product =>
      product.id === productId
        ? { ...product, quantity: newQuantity }
        : product
    ));
  }, []);

  // Update product sell price locally
  const updateProductSellPrice = useCallback((productId, newPrice) => {
    const price = parseFloat(newPrice) || 0;
    
    setProducts(prev => prev.map(product =>
      product.id === productId
        ? { ...product, sellPrice: price }
        : product
    ));
  }, []);

  // Add new product locally
  const addProduct = useCallback((newProduct) => {
    // Ensure the product has the correct structure for the cart
    const productToAdd = {
      id: newProduct.id, // Use the actual product ID from the selected product
      productId: newProduct.id, // Also store as productId for API consistency
      name: newProduct.name,
      brand: newProduct.brand || '',
      model: newProduct.model || '',
      category: newProduct.category || 'Mobile',
      quantity: newProduct.quantity || 1,
      sellPrice: parseFloat(newProduct.sellPrice) || 0,
      buyPrice: parseFloat(newProduct.buyPrice) || 0,
      flatDiscount: 0,
      effectivePrice: parseFloat(newProduct.sellPrice) || 0,
      subTotal: (parseFloat(newProduct.sellPrice) || 0) * (newProduct.quantity || 1),
      discountedSubTotal: (parseFloat(newProduct.sellPrice) || 0) * (newProduct.quantity || 1),
      image: newProduct.image || null,
      unique_id: newProduct.unique_id || null, // Include unique_id if available
      in_stock: newProduct.in_stock || 0, // Include in_stock for stock validation
      // Include product details for display
      ram: newProduct.ram || '',
      storage: newProduct.storage || '',
      variants: newProduct.variants || [],
      suffix: newProduct.suffix || '',
      used_mobile: newProduct.used_mobile
    };
    
    setProducts(prev => [productToAdd, ...prev]);
  }, []);

  // Delete product locally
  const deleteProduct = useCallback((productId) => {
    setProducts(prev => prev.filter(p => p.id !== productId));
  }, []);

  // Load data on mount
  useEffect(() => {
    if (saleId) {
      loadSaleData();
    }
  }, [saleId, loadSaleData]);

  return {
    sale,
    products,
    loading,
    error,
    actionLoading,
    
    // Actions
    handlePublishSale,
    handleCancelSale,
    handleUpdateStatus,
    updateProductQuantity,
    updateProductSellPrice,
    addProduct,
    deleteProduct,
    refreshData: () => loadSaleData(true),
    
    // State setters for external updates
    setSale,
    setProducts
  };
};
